import {common, priority, wcfclient} from '@priority-software/priority-proto-api/lib';
import {useEffect, useState} from 'react';
import {useQuery} from 'react-query';
import {debounce} from 'lodash';

import {getAdditionalProps} from '../../../../../../services/proto/core/loginProps';
import {ISearchCondition} from '../../../../types/filters';
import {ISearchField} from '../../../../../../common/searchType';
import {getLang} from '../../../../../../Localization';
import {getUsername} from '../../../../../../_shared/GWTBridge/invoke/genPage';
import {getLocalizedText, texts} from '../../../../../../constants/TextConst';
import {APP_SEARCH_CURR_USER} from '../../../../consts';
import {
    getFormSystemBooleanFlag,
    setFormSystemFlags
} from '../../../../../Form/actions/formSystemValues';

const debounceTimeout = 400;

type TSearchOption = {
    value: string;
    label: string;
    description: string;
    description2: string;
};

type TSearchFieldData = {
    options: TSearchOption[];
    haveMore?: boolean;
};

const fetchSearchFieldData = async (
    requestParams: priority.netitems.form.IFieldSelectRq
): Promise<TSearchFieldData> => {
    const reqParams = new priority.netitems.form.FieldSelectRq(requestParams);
    const {Values, HaveMore} = await wcfclient.fieldSelect(reqParams);

    const isHeb = getLang() === '1';
    const options = Values.map(value => ({
        value: value.Value,
        label: value.Value,
        description: isHeb ? value.Description2 : value.Description,
        description2: isHeb ? value.Description : value.Description2
    }));

    return {options, haveMore: HaveMore};
};

interface Props {
    filter: ISearchCondition;
    column: ISearchField;
    onSelect: (value: string) => void;
    onRemoveValue: (value: string) => void;
    session: string;
}

const useSearchControl = ({filter, column, session, onRemoveValue, onSelect}: Props) => {
    const [options, setOptions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isOldTriggerChoose, setIsOldTriggerChoose] = useState(false);

    const [requestParams, setRequestParams] = useState({
        Session: session,
        ColumnID: filter.columnID,
        Properties: getAdditionalProps(),
        Page: 1,
        FromStart: getFormSystemBooleanFlag('fieldSearchFromStart'),
        IgnoreCase: getFormSystemBooleanFlag('fieldSearchIgnoreCase'),
        Pattern: '',
        Preferred: column.isChoose
            ? priority.netitems.shared.FieldSelectType.Choose
            : priority.netitems.shared.FieldSelectType.SearchAll
    });

    const {
        data: fields,
        isLoading: isLoadingData,
        isFetching,
        refetch: getFields
    } = useQuery(
        ['searchField', filter.columnID, session, requestParams.Page],
        () => fetchSearchFieldData(requestParams),
        {
            initialData: {options: []},
            keepPreviousData: true,
            onSuccess: data => {
                if (
                    requestParams.Page === 1 &&
                    !requestParams.Pattern &&
                    data.haveMore &&
                    column.isChoose &&
                    !isOldTriggerChoose
                ) {
                    setIsOldTriggerChoose(true);
                }
            },
            select: data => {
                if (getUsername() === column.defaultValue && !requestParams.Pattern) {
                    const currentUser = {
                        value: APP_SEARCH_CURR_USER,
                        label: '',
                        description: getLocalizedText(texts.CurrentUserSearch),
                        description2: ''
                    };
                    return {...data, options: [currentUser, ...data?.options]};
                }
                return data;
            }
            // enabled: isOpen
        }
    );

    const debouncedRequest = debounce(async () => {
        await getFields();
    }, debounceTimeout);

    const onScrollEnd = () => {
        if (fields.haveMore) {
            setRequestParams(prev => ({...prev, Page: prev.Page + 1}));
        }
    };

    useEffect(() => {
        if (requestParams.Page > 1) {
            setOptions(prev => [...prev, ...fields.options]);
        } else {
            setOptions(fields.options);
        }
    }, [fields]);

    const updateRequestParams = () => {
        if (requestParams.Page === 1) {
            debouncedRequest();
        } else {
            setRequestParams(prev => ({...prev, Page: 1}));
        }
    };

    const filterChooseList = () => {
        setOptions(
            fields.options.filter(
                option =>
                    option.value.toLowerCase().includes(requestParams.Pattern.toLowerCase()) ||
                    option.description
                        ?.toLowerCase()
                        .includes(requestParams.Pattern.toLowerCase()) ||
                    option.description2?.toLowerCase().includes(requestParams.Pattern.toLowerCase())
            )
        );
    };

    useEffect(() => {
        if (column.isChoose && !isOldTriggerChoose) {
            filterChooseList();
        } else {
            updateRequestParams();
        }
    }, [requestParams.Pattern, requestParams.Preferred]);

    useEffect(() => {
        if (requestParams.Pattern) {
            if (column.isChoose && !isOldTriggerChoose) {
                filterChooseList();
            } else {
                updateRequestParams();
            }
        }
    }, [requestParams.FromStart, requestParams.IgnoreCase]);

    const handlePatternChange = (Pattern: string) => {
        const trimmedPattern = !!Pattern.trim() ? Pattern : '';
        setRequestParams(prev => ({...prev, Pattern: trimmedPattern}));
        return trimmedPattern;
    };

    const handleToggleOption = (value: string) => {
        isSelected(value) ? onRemoveValue(value) : onSelect(value);
    };

    const handleToggleCaseSensitive = () => {
        setRequestParams(prev => {
            const IgnoreCase = !prev.IgnoreCase;
            setFormSystemFlags({
                fieldSearchIgnoreCase: new common.Constant({Boolean: IgnoreCase})
            });
            return {...prev, IgnoreCase: IgnoreCase};
        });
    };

    const handleToggleFromStart = () => {
        setRequestParams(prev => {
            const FromStart = !prev.FromStart;
            setFormSystemFlags({
                fieldSearchFromStart: new common.Constant({Boolean: FromStart})
            });
            return {...prev, FromStart};
        });
    };

    const isSelected = (value: string) => {
        return filter.values.includes(value);
    };

    const handleSearchByChange = () => {
        setRequestParams(prev => {
            const Preferred =
                prev.Preferred === priority.netitems.shared.FieldSelectType.Choose
                    ? priority.netitems.shared.FieldSelectType.SearchName
                    : priority.netitems.shared.FieldSelectType.Choose;

            return {...prev, Preferred};
        });
    };

    const isSearchByName =
        requestParams.Preferred !== priority.netitems.shared.FieldSelectType.SearchName;
    const isLoading = isFetching || isLoadingData;
    return {
        options,
        requestParams,
        isLoading,
        isOpen,
        isSearchByName,
        isOldTriggerChoose,
        handlePatternChange,
        handleToggleOption,
        onScrollEnd,
        handleToggleCaseSensitive,
        handleToggleFromStart,
        isSelected,
        setIsOpen,
        handleSearchByChange
    };
};

export default useSearchControl;
